import { AbsoluteModelPath, createModelPath, ModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { CalculationFunctionBase } from "../AutoCalculations/CalculationFunctionBase";
import { CalculationFunctionDependency } from "../CalculationFunctionDependency";
import { CalculationTarget, CalculationTargetAttribute } from "../CalculationTarget";
import { IEvaluationContext } from "../IEvaluationContext";
import { ModelAccessor, ModelCountAccessor } from "../../../../Controls/withEvaluators";
import { getValueByPath } from "../../../../Helpers/StateAccessors/GetValueByPath";
import { count } from "../../../../../../Common/IterableUtils";

export type ValidationFunctionEvaluator = (
    evaluationContext: IEvaluationContext,
    targetPath: ModelPath
) => ValidationFunctionResult | Promise<ValidationFunctionResult>;

export type ValidationFunctionResult =
    | { valid: true }
    | {
          valid: false;
          message: string;
          severity: ValidationSeverity;
      };

export enum ValidationSeverity {
    Error = "Error",
    Warning = "Warning",
}

export class ValidationFunctionResults {
    public static Valid(): ValidationFunctionResult {
        return { valid: true };
    }

    public static Invalid(
        message: string,
        severity: ValidationSeverity = ValidationSeverity.Error
    ): ValidationFunctionResult {
        return { valid: false, message: message, severity: severity };
    }
}

export class ValidationFunction extends CalculationFunctionBase {
    private readonly calculationTarget: CalculationTarget;
    private readonly dependencies: readonly CalculationFunctionDependency[];
    private readonly evaluator: ValidationFunctionEvaluator;
    private readonly source?: string;
    private readonly allDependencies: readonly CalculationFunctionDependency[];

    public constructor(
        targetPath: AbsoluteModelPath,
        dependenciesPaths: AbsoluteModelPath[],
        evaluator: ValidationFunctionEvaluator,
        source?: string
    ) {
        super(targetPath);
        this.source = source;
        this.calculationTarget = new CalculationTarget(targetPath, CalculationTargetAttribute.Validation);
        this.dependencies = dependenciesPaths.map(x => new CalculationFunctionDependency(targetPath, x));
        this.evaluator = evaluator;
        this.allDependencies = [
            ...this.dependencies,
            new CalculationFunctionDependency(this.targetPath, this.targetPath.toCurrentIteration()),
        ];
    }

    public getDependencies(): readonly CalculationFunctionDependency[] {
        return this.allDependencies;
    }

    public static conditionalValidatorEvaluator(
        validateIfCondition: undefined | ((evaluationContext: IEvaluationContext) => boolean),
        evaluator: ValidationFunctionEvaluator
    ): ValidationFunctionEvaluator {
        if (validateIfCondition == null) {
            return evaluator;
        }
        return (context, targetPath) => {
            if (validateIfCondition(context)) {
                return evaluator(context, targetPath);
            }
            return ValidationFunctionResults.Valid();
        };
    }

    public static javascriptSugarExpressionToEvaluator(
        form: IForm,
        conditionContextAndDeps: Array<
            [
                condition: (path: ModelAccessor, count: ModelCountAccessor) => boolean,
                conditionContext: AbsoluteModelPath,
            ]
        >
    ): undefined | ((evaluationContext: IEvaluationContext) => boolean) {
        if (conditionContextAndDeps.length == 0) {
            return undefined;
        }
        const condition = (evaluationContext: IEvaluationContext) =>
            conditionContextAndDeps
                .map(([generatedExpressionFn, contextCurrentElement]) => {
                    const getValueByPathGeneratedFn: ModelAccessor = (expressionPath, fieldName) => {
                        if (expressionPath.startsWith("@")) {
                            const state = form.store.getState();
                            return getValueByPath(state, () => "", {}, expressionPath, fieldName);
                        }

                        const generatedAbsoluteModelPath = contextCurrentElement
                            .joinWith(createModelPath(expressionPath, "auto"))
                            .normalize();

                        if (fieldName === "children") {
                            const instances = evaluationContext.getInstances(generatedAbsoluteModelPath);
                            return Array.isArray(instances) ? (instances as string[]) : [...instances];
                        }

                        return evaluationContext.get(generatedAbsoluteModelPath) ?? "";
                    };

                    const getCountByPathGeneratedFn: ModelCountAccessor = expressionPath => {
                        const generatedAbsoluteModelPath = contextCurrentElement
                            .joinWith(createModelPath(expressionPath, "auto"))
                            .normalize();
                        return count(evaluationContext.getInstances(generatedAbsoluteModelPath));
                    };

                    return generatedExpressionFn(getValueByPathGeneratedFn, getCountByPathGeneratedFn);
                })
                .reduce((x, y) => x && y, true);
        return condition;
    }

    public async execute(context: IEvaluationContext): Promise<void> {
        const evaluator = this.evaluator;
        const targetPath = this.targetPath;
        if (context.isIgnoredPath(targetPath)) {
            return;
        }
        const evaluationResult = await evaluator(context, context.resolvePath(targetPath));
        if (evaluationResult.valid || evaluationResult.severity === ValidationSeverity.Error) {
            context.setError(targetPath, evaluationResult.valid ? undefined : evaluationResult.message);
        } else {
            context.setWarning(targetPath, evaluationResult.message);
        }
    }

    public isDependsOn(calculationTarget: CalculationTarget): boolean {
        return (
            calculationTarget.targetAttribute === CalculationTargetAttribute.Value &&
            this.dependencies.some(x => x.isMatchToPath(calculationTarget.modelPath))
        );
    }

    public getTarget(): CalculationTarget {
        return this.calculationTarget;
    }

    public asDebugString(): string {
        return `KCXmlValidationFunction(\n${this.source}\n)`;
    }
}
